import { Config } from 'config/envConfig';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCaretLeft,
    faClipboardList,
    faFilePen,
    faPlus,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
    alert,
    AlertType,
    Button,
    ConfirmModal,
    LoadingView,
} from '@quyva/shared-ui';
import { Features, OtherFeatures, isFeatureEnabled } from '@quyva/common';
import { Brand } from '../../brand';
import { UserDetailAndAction } from '../../userDetailAndAction';
import { getUrl } from '../../pdfDisplay/utils';
import { useFeatureFlags } from '../../../context';
import FormsModeToggle from './formsModeToggle';

/**
 * Sidebar list of the current user's forms. Mirrors the Templates/Layouts
 * sidebar: a header with create/collapse actions, a scrollable list of forms,
 * and the shared user footer. Clicking a form loads it into the Form Designer
 * for editing; the create button opens a blank designer.
 */
const FormsSideView = ({
    sidebarMode,
    setSidebarMode,
    selectedFormId,
    onSelectForm,
    onCreateNew,
    refreshKey,
    setIsRepoViewExpanded,
    closeSidebar,
    planName,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const user = JSON.parse(localStorage.getItem('current'));
    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);

    const baseUrl = `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/forms`;

    const fetchForms = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                getUrl(`${baseUrl}/list?email=${encodeURIComponent(user.email)}`)
            );
            setForms(response.data?.forms || []);
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.detail || 'Unable to load forms.'
            );
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.email]);

    useEffect(() => {
        fetchForms();
    }, [fetchForms, refreshKey]);

    const handleSelectForm = async (form) => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                getUrl(
                    `${baseUrl}/read?email=${encodeURIComponent(
                        user.email
                    )}&form_id=${form.form_id}`
                )
            );
            onSelectForm(response.data?.form);
            if (window.innerWidth < 1024 && closeSidebar) {
                closeSidebar();
            }
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.detail || 'Unable to open form.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteForm = async () => {
        if (!formToDelete) return;
        try {
            setIsLoading(true);
            await axios.delete(getUrl(`${baseUrl}/delete`), {
                data: { email: user.email, form_id: formToDelete.form_id },
            });
            if (selectedFormId === formToDelete.form_id) {
                onCreateNew();
            }
            await fetchForms();
            alert(AlertType.SUCCESS, 'Form deleted.');
        } catch (error) {
            alert(
                AlertType.ERROR,
                error.response?.data?.detail || 'Unable to delete form.'
            );
        } finally {
            setFormToDelete(null);
            setIsLoading(false);
        }
    };

    return (
        <div className='z-0 h-full w-full flex flex-col bg-transparent p-1 m-0 rounded-xl text-texts-secondary300'>
            {/* Header */}
            <div className='w-full flex flex-col items-center gap-2 pb-2 mb-2 border-b-2 border-b-backgrounds-white'>
                {isFeatureEnabled(
                    [Features.OTHERS, OtherFeatures.LOGO],
                    featureFlagsData
                ) && <Brand />}

                <FormsModeToggle mode={sidebarMode} setMode={setSidebarMode} />

                <div className='w-full flex items-center justify-between'>
                    <div className='flex items-center truncate'>
                        <FontAwesomeIcon
                            icon={faClipboardList}
                            className='mr-2 drop-shadow-sm text-icons-primary'
                        />
                        <div className='font-semibold truncate text-base'>
                            Forms
                        </div>
                    </div>
                    <div className='flex items-center justify-center'>
                    <Button
                        icon={faPlus}
                        className={'mr-2 w-9 h-9 text-icons-primary'}
                        onClick={onCreateNew}
                        hoverText={'Create New Form'}
                        type={'success'}
                    />
                    <Button
                        icon={faCaretLeft}
                        className={'w-9 h-9 text-icons-primary'}
                        onClick={() => {
                            if (window.innerWidth > 1024) {
                                setIsRepoViewExpanded(false);
                            } else if (closeSidebar) {
                                closeSidebar();
                            }
                        }}
                        hoverText={'Collapse and hide Forms view'}
                        type={'warning'}
                    />
                    </div>
                </div>
            </div>

            {/* List */}
            <div className='flex-1 min-h-0 overflow-auto'>
                {isLoading ? (
                    <div className='w-full h-32 flex justify-center items-center py-2'>
                        <LoadingView loadingText={'Loading forms...'} />
                    </div>
                ) : forms.length === 0 ? (
                    <div className='w-full flex flex-col justify-center items-center py-8 text-texts-secondary300'>
                        <FontAwesomeIcon
                            icon={faClipboardList}
                            className='w-12 h-12 mb-2 drop-shadow text-icons-primary100'
                        />
                        <div className='text-center'>No forms available</div>
                        <div className='flex items-center mt-4 text-texts-secondary300'>
                            <FontAwesomeIcon
                                icon={faPlus}
                                className='mr-2 text-icons-primary'
                            />
                            <div>Create a new form</div>
                        </div>
                    </div>
                ) : (
                    forms.map((form) => {
                        const isSelected = selectedFormId === form.form_id;
                        return (
                            <div
                                key={form.form_id}
                                className={`w-full flex items-center justify-between px-2 py-0.5 my-2 rounded-xl border border-transparent hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 ${
                                    isSelected
                                        ? 'bg-backgrounds-primary100 border-backgrounds-primary200 shadow-inner'
                                        : ''
                                }`}
                            >
                                <button
                                    type='button'
                                    className='flex items-center justify-start truncate flex-1 text-left py-1'
                                    onClick={() => handleSelectForm(form)}
                                    title={form.form_name}
                                >
                                    <FontAwesomeIcon
                                        icon={faFilePen}
                                        className={`mr-2 drop-shadow-sm ${
                                            isSelected
                                                ? 'text-backgrounds-primary300'
                                                : 'text-icons-primary'
                                        }`}
                                    />
                                    <span
                                        className={`truncate text-base ${
                                            isSelected
                                                ? 'text-backgrounds-primary300'
                                                : ''
                                        }`}
                                    >
                                        {form.form_name}
                                    </span>
                                </button>
                                <Button
                                    icon={faTrash}
                                    className='ml-2'
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setFormToDelete(form);
                                    }}
                                    hoverText={'Delete Form'}
                                    type={'danger'}
                                />
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div className='w-full h-28 border-t-2 border-t-backgrounds-white pt-1'>
                <UserDetailAndAction planName={planName} />
            </div>

            {formToDelete && (
                <ConfirmModal
                    modalText={`Are you sure you want to delete the form "${formToDelete.form_name}"?`}
                    onConfirm={handleDeleteForm}
                    onCancel={() => setFormToDelete(null)}
                />
            )}
        </div>
    );
};

export default FormsSideView;
